import { useParams, useNavigate } from 'react-router-dom';
import { ChartBarIcon, CheckBadgeIcon, ChevronLeftIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useComms } from '../hooks/useComms';
import { Detail, Bar } from './CommsDispatch';

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

// Message analytics — mirrors training's Course Insights: a list of everything
// sent; picking one opens its read/acknowledge dashboard as a FULL PAGE at
// /comms/analytics/:id (not a modal), reusing the Detail view from Comms
// Dispatch (read-over-time chart + CSV/PDF export + chase unread).
function MessageAnalytics() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: comms, isLoading } = useComms();

  // Full-page detail for a single message.
  if (id) return <Detail id={Number(id)} onBack={() => navigate('/comms/analytics')} />;

  return (
    <div className="p-6">
      <button type="button" onClick={() => navigate('/?dept=communications')} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to Communications
      </button>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
          <ChartBarIcon className="h-7 w-7 text-brand" /> Message Analytics
        </h1>
        <button type="button" onClick={() => navigate('/comms/recipients')} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-brand hover:text-brand">
          <UsersIcon className="h-4 w-4" /> View by recipient
        </button>
      </div>
      <p className="mb-6 text-sm text-gray-500">Read and acknowledgement analytics for every message sent. Select a message to open its dashboard, then export CSV or PDF. Or see it <span className="font-medium text-gray-600">by recipient</span> to track one person's engagement over time.</p>

      {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
      {comms && (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2.5">Subject</th><th className="px-4 py-2.5">Audience</th>
                <th className="px-4 py-2.5 text-right">Recipients</th><th className="px-4 py-2.5">Read</th>
                <th className="px-4 py-2.5">Ack</th><th className="px-4 py-2.5">Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {comms.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-brand-light/40" onClick={() => navigate(`/comms/analytics/${c.id}`)}>
                  <td className="px-4 py-2.5 text-sm font-medium text-gray-800">
                    {c.priority === 'high' && <span className="mr-1 rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-600">High</span>}
                    {c.subject}
                    {c.requires_ack ? <CheckBadgeIcon className="ml-1 inline h-4 w-4 text-emerald-500" title="Requires acknowledgement" /> : null}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{c.audience_summary}</td>
                  <td className="px-4 py-2.5 text-right text-sm text-gray-600">{c.recipient_count}</td>
                  <td className="px-4 py-2.5"><Bar value={c.read_count} total={c.recipient_count} /></td>
                  <td className="px-4 py-2.5">{c.requires_ack ? <Bar value={c.ack_count} total={c.recipient_count} tone="green" /> : <span className="text-xs text-gray-300">—</span>}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(c.created_at)}</td>
                </tr>
              ))}
              {comms.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-400">No messages sent yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default MessageAnalytics;
